/** @jsxImportSource @emotion/react */

import { useRef } from 'react';
import { CSSObject } from '@emotion/react';
import { IAction } from '../../../hooks/useAction';

import { IBasePart, IMargins } from '../part';
import Clickable from '../../../components/clickable';
import useStatefulInput, {
  IInputValidator,
} from '../../../hooks/useStatefulInput';
import RemoteImage, { IRemoteImage } from '../../../components/RemoteImage';
import InputValidationError from '../components/inputValidationError';
import useLanguage from '../../../hooks/useLanguage';

export interface IRatingInputPart extends IBasePart {
  type: 'rating_input';

  inputId: string;
  margins: IMargins;

  items: [
    {
      itemId: string;
      idleImage: IRemoteImage;
      activeImage: IRemoteImage;
      margins: IMargins;

      size: {
        height: number;
        width: number;
      };

      action: IAction;
    }
  ];

  selectedItemId?: string;
  inputAccessibility?: { legend: string; legendIdentifier: string };
  noItemSelectedAccessibilityLabel: string;

  readOnly: boolean;
  isExclusive: boolean;
  validators?: Array<IInputValidator>;

  action: IAction;
}

const RatingInputPart = (data: IRatingInputPart) => {
  const {
    inputValue,
    isValid,
    isDirty,
    isTouched,
    errorMessage,
    setInputValue,
    setIsTouched,
  } = useStatefulInput<string | null>({
    inputId: data.inputId,
    initialValue: data.selectedItemId || null,
    validators: data.validators,
  });
  const selectedIdsRef = useRef<string[]>([]);
  const language = useLanguage();

  const selectedIndex = data.items.findIndex(
    (item) => item.itemId === inputValue
  );
  const shouldShowErrorState = !isValid && isDirty && isTouched;

  const OUTER_WRAPPER_STYLE: CSSObject = {
    marginTop: data.margins.top,
    marginRight: data.margins.trailing,
    marginBottom: data.margins.bottom,
    marginLeft: data.margins.leading,
  };

  const INNER_WRAPPER_STYLE: CSSObject = {
    display: 'flex',
    justifyContent: 'center',
  };

  const handleSelect = (item: string, index: number) => {
    const selectedIds = selectedIdsRef.current;
    const isAlreadySelected = selectedIds.includes(item);

    // prevent deselecting first item once selected you cannot go back to no star selected state
    if (isAlreadySelected && item === '1') {
      return;
    }

    if (isAlreadySelected) {
      selectedIdsRef.current = selectedIds.filter((id) => id !== item);
      setInputValue(data.items[index - 1]?.itemId ?? null);
    } else {
      selectedIdsRef.current.push(item);
      setInputValue(item);
    }
  };
  const isSelected = inputValue && selectedIndex >= 0;
  const ariaLabel = !isSelected
    ? data.noItemSelectedAccessibilityLabel
    : data.inputAccessibility?.legend ?? '';

  return (
    <div css={OUTER_WRAPPER_STYLE} aria-label={ariaLabel}>
      <div
        css={INNER_WRAPPER_STYLE}
        role='radiogroup'
        aria-labelledby={data.inputAccessibility?.legendIdentifier}
        tabIndex={0}
      >
        {data.items.map((item, i) => (
          <Clickable
            id={item.itemId}
            role='radio'
            renderAs='button'
            key={i}
            scale
            css={{ width: 'auto' }}
            action={item.action}
            onClick={() => handleSelect(item.itemId, i)}
            onBlur={() => {
              setIsTouched();
            }}
            aria-checked={inputValue === item.itemId}
          >
            {/* When 'isExclusive' is set, only the item which has been chosen should be
                be set as active. Else all the items up to and including the chosen item
                should be set as active. Other items should be set as idle.
            */}
            <RemoteImage
              {...(data.isExclusive
                ? i === selectedIndex
                  ? { ...item.activeImage }
                  : { ...item.idleImage }
                : i <= selectedIndex
                ? { ...item.activeImage }
                : { ...item.idleImage })}
              css={{
                width: item.size.width,
                height: item.size.height,
              }}
              accessibilityLabel={`${
                item.activeImage.accessibilityLabel
              } ${language.get('accessibility_rating_component')}`}
            />
          </Clickable>
        ))}
      </div>

      {/* Validation error message */}
      {shouldShowErrorState && (
        <InputValidationError inputId={data.inputId} message={errorMessage} />
      )}
    </div>
  );
};

export default RatingInputPart;
